package br.com.htms.inspeccaocampo.data

import br.com.htms.inspeccaocampo.platform.currentTimeMillis
import br.com.htms.inspeccaocampo.platform.formatDateBr

/**
 * Estrutura do Excel: CHECKLIST - ALOJAMENTO.xlsx (itens 1–60 + rodapé).
 * Sem coleta de fotos. Itens ativos/títulos vêm de [AppConfig].
 */
object ChecklistAlojamentoCatalog {

    private var configAtual: AppConfig? = null

    fun recarregar(config: AppConfig) {
        configAtual = config
    }

    /** Catálogo estático completo (antes do filtro de config). */
    fun itensBase(): List<ItemConformidade> = secoesBase().flatMap { it.itens }

    fun secoesBase(): List<SecaoConformidade> = listOf(
        secao(
            "Estrutura do Alojamento",
            1 to "Construção em boas condições (sem infiltrações, rachaduras)",
            2 to "Pé-direito adequado (mín. ~2,5 m recomendado)",
            3 to "Piso lavável e em bom estado",
            4 to "Paredes íntegras e higienizadas",
            5 to "Cobertura sem goteiras",
            6 to "Boa ventilação natural ou artificial",
            7 to "Iluminação adequada (natural e/ou artificial)"
        ),
        secao(
            "Dormitórios",
            8 to "Camas individuais",
            9 to "Colchão com densidade 26 (vinte e seis) e espessura mínima de 0,10m",
            10 to "Distância mínima entre camas (~1 m recomendado)",
            11 to "Colchões em boas condições (higienizados)",
            12 to "Roupa de cama fornecida e limpa",
            13 to "Armários individuais para pertences com dimensões mínimas de 1,20m de altura por 0,30m de largura, e 0,40 de profundidade",
            14 to "Limite de ocupação respeitado"
        ),
        secao(
            "Instalações Sanitárias",
            15 to "Separação por sexo (quando aplicável)",
            16 to "Quantidade adequada de vasos sanitários",
            17 to "Lavatórios com água corrente",
            18 to "Sabonete líquido disponível",
            19 to "Papel toalha ou secador",
            20 to "Chuveiros em número suficiente",
            21 to "Água quente",
            22 to "Ralos e escoamento funcionando",
            23 to "Limpeza frequente registrada",
            24 to "Possui suporte de papel higiênico, toalha, sabonete e shampoo",
            25 to "A lixeira do banheiro possui tampa?"
        ),
        secao(
            "Área de Refeição",
            26 to "Local separado dos dormitórios",
            27 to "Mesas e assentos suficientes",
            28 to "Ambiente limpo e organizado",
            29 to "Proteção contra poeira/insetos",
            30 to "Lixeiras com tampa",
            31 to "Água potável disponível",
            32 to "Local adequado para armazenamento de alimentos"
        ),
        secao(
            "Água Potável",
            33 to "Fornecimento contínuo de água potável",
            34 to "Bebedouros em boas condições",
            35 to "Copos individuais ou descartáveis",
            36 to "Análise de potabilidade"
        ),
        secao(
            "Limpeza e Conservação",
            37 to "Rotina de limpeza definida",
            38 to "Produtos de limpeza disponíveis",
            39 to "Responsável pela limpeza definido",
            40 to "Controle de pragas (dedetização)",
            41 to "Controle de limpeza"
        ),
        secao(
            "Segurança",
            42 to "Extintores disponíveis e válidos",
            43 to "Sinalização de emergência",
            44 to "Iluminação de emergência",
            45 to "Saídas de emergência desobstruídas",
            46 to "Instalação elétrica segura (NR-10)",
            47 to "Proibição de gambiarras elétricas",
            48 to "Quadro elétrico sinalizado"
        ),
        secao(
            "Lavanderia / Higiene Pessoal",
            49 to "Local para lavagem de roupas",
            50 to "Tanques ou máquinas disponíveis",
            51 to "Área para secagem de roupas",
            52 to "Ambiente ventilado"
        ),
        secao(
            "Gestão de Resíduos",
            53 to "Lixeiras adequadas e identificadas",
            54 to "Coleta regular de resíduos",
            55 to "Destinação correta dos resíduos"
        ),
        secao(
            "Documentação e Gestão",
            // Excel marca "56 Documentação e Gestão" como linha numerada + 57–60.
            56 to "Documentação e Gestão (geral)",
            57 to "Procedimento de uso do alojamento",
            58 to "Registro de limpeza",
            59 to "Controle de ocupação",
            60 to "Inspeções periódicas registradas"
        )
    )

    fun secoesPadrao(): List<SecaoConformidade> {
        val ativos = itensPadrao()
        return secoesBase().mapNotNull { secao ->
            val itens = ativos.filter { it.secao == secao.titulo }
            if (itens.isEmpty()) null else secao.copy(itens = itens)
        }
    }

    fun itensPadrao(): List<ItemConformidade> {
        val byId = configAtual?.camposAlojamentoChecklist?.associateBy { it.id }.orEmpty()
        return itensBase().mapNotNull { base ->
            val cfg = byId[base.id]
            when {
                byId.isEmpty() || cfg == null -> base
                !cfg.ativo -> null
                else -> base.copy(
                    titulo = cfg.titulo.ifBlank { base.titulo },
                    obrigatorio = cfg.obrigatorio,
                    fotoObrigatoria = cfg.fotoObrigatoriaSeSim,
                    fotoHabilitada = cfg.fotoObrigatoriaSeSim || base.fotoHabilitada
                )
            }
        }
    }

    private fun secao(titulo: String, vararg pares: Pair<Int, String>) =
        SecaoConformidade(
            titulo = titulo,
            itens = pares.map { (n, t) ->
                ItemConformidade(
                    id = "ck_$n",
                    numero = n.toString(),
                    titulo = t,
                    secao = titulo,
                    fotoHabilitada = false,
                    fotoObrigatoria = false
                )
            }
        )
}

data class AssinaturaChecklist(
    val nome: String = "",
    val data: String = "",
    val assinatura: String = ""
)

data class AcaoCorrecaoChecklist(
    val item: String = "",
    val acao: String = "",
    val responsavel: String = "",
    val prazo: String = ""
)

data class FormChecklistAlojamento(
    val endereco: String = "",
    val obra: String = "",
    val dataInspecao: String = "",
    val horaInspecao: String = "",
    val dataHorarioMs: Long = currentTimeMillis(),
    val latitude: Double? = null,
    val longitude: Double? = null,
    val localTexto: String = "",
    val itens: List<ItemConformidade> = ChecklistAlojamentoCatalog.itensPadrao(),
    val liberadoParaUso: RespostaSimNao? = null,
    val acoesCorrecao: List<AcaoCorrecaoChecklist> = emptyList(),
    val inspecionadoPor: AssinaturaChecklist = AssinaturaChecklist(),
    val acompanhadoPor: AssinaturaChecklist = AssinaturaChecklist(),
    val engenheiroResidente: AssinaturaChecklist = AssinaturaChecklist(),
    val numeroTrabalhadores: String = "",
    val dataVerificacao: String = ""
) {
    fun dataHorarioFormatado(): String = formatDateBr(dataHorarioMs)
}

fun FormChecklistAlojamento.temConteudoRascunho(): Boolean =
    endereco.isNotBlank() ||
        obra.isNotBlank() ||
        itens.any { it.resposta != null || it.justificativa.isNotBlank() } ||
        liberadoParaUso != null ||
        inspecionadoPor.nome.isNotBlank() ||
        acompanhadoPor.nome.isNotBlank() ||
        engenheiroResidente.nome.isNotBlank() ||
        numeroTrabalhadores.isNotBlank()
